// src/sim/controllers/ai-controller.ts — P4 Plan 06 AIController with role dispatch.
//
// Holds per-entity mistreevous BehaviourTree instances (hunterTree + hiderTree +
// dogTree + adultTree) with seeded RNG, per-role decide cadence (D-05), and
// ZERO_INTENT guards for caught/immune entities.
//
// Design invariants:
//   - Pitfall P4-D: BOTH `random` and `getDeltaTime` always passed to BehaviourTree
//     constructor so `wait` nodes never fall back to Date.now().
//   - D-05: per-role cadence via nextDecideTick field; cached intent returned between decides.
//   - D-06: per-entity RNG sub-stream injected at construction; entityRng is the sole source.
//   - D-07: all four tree instances held per entity; decide() selects by role reference.
//     Tree instances survive role flips (never re-parsed); reset() clears all four (T-04-16).
//     WR-11: forward-compat for team-switch — dog/adult never flip roles, but
//     holding all four trees uniformly keeps the controller class simple and
//     the reset path symmetric. A role-gated construction optimisation is
//     deferred (the parse cost is bounded — ~4 entities × 2 redundant trees
//     at roster size, dwarfed by per-tick decide cost).
//   - D-32 / AI-16: ZERO_INTENT returned during freedImmunityUntilTick window.
//   - T-04-13 / Pitfall P4-C: AgentContext.world is `never`; BT actions cannot access WorldState.
//
// Source: .planning/phases/04-ai/04-06-PLAN.md Task 2;
//         04-CONTEXT.md D-05, D-06, D-07, D-32;
//         04-RESEARCH.md Pattern 3 L478-530, Pattern 5 L610-618.

import { BehaviourTree } from 'mistreevous';
import hunterMdsl from '../ai/hunter.bt.mdsl?raw';
import hiderMdsl from '../ai/hider.bt.mdsl?raw';
import dogMdsl from '../ai/dog.bt.mdsl?raw';
import adultMdsl from '../ai/adult.bt.mdsl?raw';
import type { CharacterController } from './controller';
import type { Perception } from '../../shared/types/perception';
import type { Intent } from '../../shared/types/intent';
import { ZERO_INTENT } from '../../shared/types/intent';
import {
  scoreHunterBranches,
  scoreHiderBranches,
  scoreDogBranches,
  scoreAdultBranches,
  argmaxBranch,
} from '../ai/utility';
import * as Conditions from '../ai/conditions';
import * as Actions from '../ai/actions';
import {
  P4_HUNTER_DECIDE_PERIOD_TICKS,
  P4_HIDER_DECIDE_PERIOD_TICKS,
  P4_DOG_DECIDE_PERIOD_TICKS,
  P4_ADULT_DECIDE_PERIOD_TICKS,
  P4_BOREDOM_BASE_TICKS,
  P4_BOREDOM_HIDE_SKILL_TICKS,
  P4_BOREDOM_ALONE_TICKS,
  P4_BOREDOM_RNG_RANGE,
} from '../ai/constants';
import type { CharacterProfile } from '../characters/character-profile';
import { P3_FLEE_FROM_BASE_CLEAR_PX } from '../catch/constants';
import type { AgentContext } from '../ai/ai-controller-types';
import { getProfile, hasProfile, DEFAULT_CHARACTER_ID } from '../characters/profiles';
import type { ZonePolygon } from '../../shared/types/map';
import { zoneCenter } from '../map/zones';
import { findTilePath } from '../map/pathfinding';

/**
 * D-13: Compute the tick threshold after which a hiding hider exits its spot.
 * Formula: base = P4_BOREDOM_BASE_TICKS + hideSkill*P4_BOREDOM_HIDE_SKILL_TICKS
 *                 - fear.alone*P4_BOREDOM_ALONE_TICKS
 * Jitter:  ±P4_BOREDOM_RNG_RANGE/2 via the seeded per-agent RNG sub-stream (never Math.random).
 * Patient profiles (high hideSkill, low fear.alone) wait longer before relocating.
 */
function computeHiderBoredThreshold(
  profile: CharacterProfile,
  rng: () => number,
): number {
  const base =
    P4_BOREDOM_BASE_TICKS
    + profile.hideSkill * P4_BOREDOM_HIDE_SKILL_TICKS
    - profile.fear.alone * P4_BOREDOM_ALONE_TICKS;
  const jitter = 1 + (rng() - 0.5) * P4_BOREDOM_RNG_RANGE;
  return Math.round(base * jitter);
}

export interface AiControllerDeps {
  entityId: string;
  entityRng: () => number;
  initialRole: 'hunter' | 'hider' | 'dog' | 'adult';
}

export class AIController implements CharacterController {
  private readonly entityRng: () => number;
  private readonly agent: AgentContext & Record<string, unknown>;
  private readonly hunterTree: BehaviourTree;
  private readonly hiderTree: BehaviourTree;
  private readonly dogTree: BehaviourTree;
  private readonly adultTree: BehaviourTree;
  private cachedIntent: Intent;
  private nextDecideTick = 0;
  /**
   * Debug session cascade-prevention (2026-04-30): the freedImmunityUntilTick
   * value seen on the LAST decide tick. When the field transitions from null
   * to non-null (= the moment of liberation), the cached intent likely still
   * points TOWARD the ring (the BT just ran the liberate branch). We force
   * an immediate re-decide on that tick so the new permanent liberate-
   * suppression takes effect on the very next tick instead of waiting for
   * the cadence to elapse — which fixes the "liberator stuck against the
   * ring for 6 ticks" symptom user-reported 2026-04-30.
   */
  private lastSeenFreedImmunity: number | null = null;
  /** Tracks the previous tick's `self.status` to detect structural transitions
   *  (e.g. escorting → free at drop-off) that should invalidate the cadence
   *  cache so the AI re-decides immediately. */
  private lastSeenStatus: string = 'free';

  constructor(deps: AiControllerDeps) {
    this.entityRng = deps.entityRng;
    this.cachedIntent = { ...ZERO_INTENT, actions: { ...ZERO_INTENT.actions } };

    // Build agent: all condition/action methods bound as plain object properties.
    // mistreevous resolves action/condition names by looking up agent[name]().
    const actionMethods: Record<string, unknown> = {};
    for (const [name, fn] of Object.entries(Actions)) {
      if (typeof fn === 'function' && name !== 'effectiveHearingRangeTiles' && name !== 'State') {
        actionMethods[name] = fn;
      }
    }
    const conditionMethods: Record<string, unknown> = {};
    for (const [name, fn] of Object.entries(Conditions)) {
      if (typeof fn === 'function') {
        conditionMethods[name] = fn;
      }
    }

    const intent: Intent = { ...ZERO_INTENT, actions: { ...ZERO_INTENT.actions } };

    this.agent = {
      perception: null as unknown as Perception,
      utilities: {} as Record<string, number>,
      winningBranch: 'patrol-far',
      intent,
      checkedHidingSpotIds: new Set<string>(),
      caughtAtBaseCount: 0,
      world: undefined as never,
      ...conditionMethods,
      ...actionMethods,
    } as AgentContext & Record<string, unknown>;

    // Pitfall P4-D: always pass BOTH random and getDeltaTime (T-04-14).
    // D-07: all four trees constructed at entity creation; decide() picks by role.
    // T-04-16: never re-parsed across role flips; reset() clears all.
    this.hunterTree = new BehaviourTree(hunterMdsl, this.agent, {
      random: this.entityRng,
      getDeltaTime: () => 1 / 30,
    });
    this.hiderTree = new BehaviourTree(hiderMdsl, this.agent, {
      random: this.entityRng,
      getDeltaTime: () => 1 / 30,
    });
    // Plan 08: dog + adult trees dispatched by role (D-04). Prefix-based fallback
    // dispatch was removed in WR-08; role is authoritative.
    this.dogTree = new BehaviourTree(dogMdsl, this.agent, {
      random: this.entityRng,
      getDeltaTime: () => 1 / 30,
    });
    this.adultTree = new BehaviourTree(adultMdsl, this.agent, {
      random: this.entityRng,
      getDeltaTime: () => 1 / 30,
    });
  }

  /** Returns the seeded per-entity RNG function (D-06 sub-stream). */
  getEntityRng(): () => number {
    return this.entityRng;
  }

  /**
   * Returns the last Intent produced by decide() (or ZERO_INTENT if decide()
   * has never run on this controller).
   *
   * Why this exists: GameScene.update() needs to feed AI-decided intents into
   * the next tick's IntentBundle so the movement stages (tick.ts Stages 3–4)
   * can consume them. tick.ts Stage 16 calls decide() internally, populating
   * cachedIntent. The next call to GameScene.update() then reads that cache
   * here, yielding a one-tick lag (~33 ms) between AI decision and movement —
   * well below human perceptual threshold and preferred over duplicating the
   * full buildPerception + decide path on the render side.
   *
   * The cadence-gated decide() path inside tick.ts returns this same cached
   * intent unchanged until nextDecideTick is reached, so there is no risk of
   * mismatch between what GameScene reads and what Stage 16 would produce.
   */
  getCachedIntent(): Intent {
    return this.cachedIntent;
  }

  decide(perception: Perception, _dt: number): Intent {
    const self = perception.self;

    // Guard: caught / non-active statuses → no AI decision
    if (
      self.status === 'caught' ||
      self.status === 'peeking' ||
      self.status === 'spectating'
    ) {
      return ZERO_INTENT;
    }

    // D-13: boredom timer for hiding hiders — commit to the spot until a seeded,
    // character-dependent threshold is exceeded, then exit via toggleHide.
    // _hidingStartTick is agent scratch only (not WorldState/Entity — golden hash unaffected).
    // Set-once pattern: set on first decide() in hiding status; cleared on exit or reset().
    if (self.status === 'hiding') {
      const scratch = this.agent as Record<string, unknown>;
      if ((scratch['_hidingStartTick'] as number | undefined) == null) {
        scratch['_hidingStartTick'] = perception.tick; // set-once
      }
      const boredProfile = getProfile(
        hasProfile(self.characterId) ? self.characterId : DEFAULT_CHARACTER_ID,
      );
      const boredAt = scratch['_hidingStartTick'] as number;
      const boredThreshold = computeHiderBoredThreshold(boredProfile, perception.rng);
      if (perception.tick - boredAt > boredThreshold) {
        scratch['_hidingStartTick'] = undefined; // clear for next hide cycle
        const exitIntent: Intent = {
          ...ZERO_INTENT,
          actions: { ...ZERO_INTENT.actions, toggleHide: true },
        };
        this.cachedIntent = exitIntent;
        return exitIntent;
      }

      // Late-round idle fix (debug session late-round-idle, 2026-07-06): if
      // teammates are parked at base AND this hider hasn't already liberated
      // (freedImmunityUntilTick === null), exit the hiding spot immediately so
      // the liberate branch can win on the next decide tick. Without this,
      // boredom (up to 877 ticks ≈ 29 s for patient hiders) is the ONLY exit,
      // making the lone free hider appear to "stand still" while 3 teammates
      // are caught at base. We only skip suppressed hiders (freedImmunityUntilTick
      // non-null) to preserve the "no re-liberate once freed" design invariant.
      const caughtAtBase = perception.round.caughtHidersAtBase ?? 0;
      const liberationSuppressed = perception.self.freedImmunityUntilTick !== null;
      if (caughtAtBase > 0 && !liberationSuppressed) {
        scratch['_hidingStartTick'] = undefined; // clear so next hide cycle starts fresh
        const urgentExitIntent: Intent = {
          ...ZERO_INTENT,
          actions: { ...ZERO_INTENT.actions, toggleHide: true },
        };
        this.cachedIntent = urgentExitIntent;
        return urgentExitIntent;
      }

      return ZERO_INTENT;
    }

    // -----------------------------------------------------------------------
    // MECHANIC 1 — TRUE FLEE-FROM-BASE (debug free-all-misfire-frozen-hider,
    // 2026-06-12). applyFreeAll stamps fleeFromBaseUntilTick on EVERY freed
    // captive AND the liberator. Until that tick (or until the hider clears the
    // ring halo), OVERRIDE the hider BT with a FULL-SPEED unit vector pointing
    // radially AWAY from the plinth centre, so freed hiders genuinely run clear
    // of the base instead of twitching / random-walking on it. Recomputed every
    // tick (placed BEFORE the cadence cache) so the direction tracks the hider's
    // moving position and stays strong + directional. The movement stage's
    // axis-separated wall clamp keeps them off obstacles (sliding along walls),
    // and radially-outward points away from the solid ring so they escape the
    // halo. Once cleared (dist > P3_FLEE_FROM_BASE_CLEAR_PX) or the deadline
    // elapses, normal hider AI resumes — the BUG#2c reachable-spot pick then
    // re-hides them properly. The human player (e0) is never routed through
    // decide(), so this never fights manual input.
    if (self.role === 'hider') {
      const fleeUntil = self.fleeFromBaseUntilTick ?? null;
      if (fleeUntil !== null && perception.tick < fleeUntil) {
        const plinthZone = perception.map.zones.get('plinth');
        const center = plinthZone
          ? zoneCenter(plinthZone)
          : {
              x: (perception.map.widthInTiles * perception.map.tileWidth) / 2,
              y: (perception.map.heightInTiles * perception.map.tileHeight) / 2,
            };
        const dx = self.position.x - center.x;
        const dy = self.position.y - center.y;
        const dist = Math.hypot(dx, dy);
        if (dist <= P3_FLEE_FROM_BASE_CLEAR_PX) {
          let move: { x: number; y: number };
          if (dist < 0.001) {
            const a = perception.rng() * Math.PI * 2;
            move = { x: Math.cos(a), y: Math.sin(a) };
          } else {
            move = { x: dx / dist, y: dy / dist };
          }
          const fleeIntent: Intent = {
            move,
            facing: Math.atan2(move.y, move.x),
            actions: { ...ZERO_INTENT.actions },
          };
          // Force a re-decide next tick so the radial vector tracks the hider's
          // new position (do NOT serve a stale cadence cache while fleeing).
          this.nextDecideTick = perception.tick;
          this.cachedIntent = fleeIntent;
          return fleeIntent;
        }
      }
    }

    // Original D-32 / AI-16 imposed a full ZERO_INTENT freeze for the entire
    // freed-immunity window. With P3_FREED_IMMUNITY_TICKS bumped to 30 (debug
    // session cascade-prevention 2026-04-28) that would freeze the freed
    // hider for 1 s — visibly stuck. Removing the freeze entirely is safe
    // because (a) the scorer-level liberate-suppression
    // (P4_POST_LIBERATION_LIBERATE_SUPPRESSION_TICKS = 90 ticks) handles
    // "don't re-trigger free-all" via the hider's own decision tree, (b)
    // catch.ts still blocks re-catch for the full immunity window, and (c)
    // letting the freed hider MOVE during immunity is the entire point —
    // they need to walk to a hiding spot before the catch-safe window ends.

    // Debug session cascade-prevention (2026-04-30): detect transitions
    // that should invalidate the cadence cache so the AI immediately picks
    // a new branch instead of replaying the stale toward-ring intent.
    //
    // 1. freedImmunityUntilTick null→non-null (or increasing) — moment of
    //    liberation. The BT just ran the liberate branch and the cached
    //    intent points TOWARD the ring; we want to switch to deep/move.
    // 2. recentDropOffTick === currentTick — moment of drop-off. The
    //    hunter's escort short-circuit just set "walk toward plinth"; we
    //    want to switch to patrol-far (Plan 06-13's post-drop behaviour).
    // 3. lastSeenStatus 'escorting'→'free' — same idea on the structural
    //    transition rather than the timestamp signal.
    const currentImmunity = perception.self.freedImmunityUntilTick ?? null;
    const justLiberated =
      currentImmunity !== null &&
      (this.lastSeenFreedImmunity === null || currentImmunity > this.lastSeenFreedImmunity);
    this.lastSeenFreedImmunity = currentImmunity;

    const justDroppedOff =
      perception.self.role === 'hunter' &&
      perception.self.recentDropOffTick !== null &&
      perception.self.recentDropOffTick === perception.tick;

    const justStoppedEscorting =
      this.lastSeenStatus === 'escorting' && perception.self.status === 'free';
    this.lastSeenStatus = perception.self.status;

    if (justLiberated || justDroppedOff || justStoppedEscorting) {
      this.nextDecideTick = perception.tick; // bypass cadence cache below
    }

    // D-05: cadence cache — return last intent until nextDecideTick
    if (perception.tick < this.nextDecideTick) {
      return this.cachedIntent;
    }

    // UAT Test 2 follow-up: escort-to-plinth short-circuit. The hunter BT has
    // no escort branch — when a hunter catches a hider, status flips to
    // 'escorting' and chase keeps winning (the caught hider is still visible
    // at the hunter's own tile). PathTowardNearestHider's A* returns null for
    // same-tile targets, leaving intent.move at zero. Hunter freezes with a
    // caught hider glued to their back and never reaches the plinth for the
    // drop-off. Bypass the BT here and steer the hunter toward the plinth
    // zone via A*. drop-off.ts triggers automatically once the hunter steps
    // inside the plinth polygon.
    if (self.role === 'hunter' && self.status === 'escorting') {
      const intent: Intent = {
        move: { x: 0, y: 0 },
        facing: self.facing,
        actions: { ...ZERO_INTENT.actions },
      };
      const plinthZone = perception.map.zones.get('plinth');
      if (plinthZone) {
        const target = this.escortTargetForPlinth(plinthZone);
        const dx = target.x - self.position.x;
        const dy = target.y - self.position.y;
        const len = Math.hypot(dx, dy);
        if (len > 0.001) {
          const next = this.escortAStarDir(self.position, target, perception);
          intent.facing = Math.atan2(dy, dx);
          if (next) intent.move = next;
        }
      }
      this.nextDecideTick = perception.tick + P4_HUNTER_DECIDE_PERIOD_TICKS;
      this.cachedIntent = intent;
      return intent;
    }

    // D-07 / Plan 08: route to active tree strictly by role.
    // WR-08 fix: removed the legacy `characterId.startsWith('dog' | 'adult')`
    // fallback — the EntityRole union already includes 'dog' | 'adult'
    // (world.ts). Keeping a string-prefix shortcut risks misdispatching any
    // future profile whose id happens to start with those letters (e.g.
    // a relative named "Dougal" or "Adalberto").
    const activeTree = determineActiveTree(perception.self, this);

    // 1. Pre-compute utilities (Pattern 3)
    let scores: Record<string, number>;
    if (perception.self.role === 'dog') {
      scores = scoreDogBranches(perception);
    } else if (perception.self.role === 'adult') {
      const agentRec = this.agent as Record<string, unknown>;
      const lastCommentaryTick = (agentRec['lastAdultCommentaryTick'] as number | null | undefined) ?? null;
      // pendingCommentableEvents typed as EventTag-keyed array; cast to scoreAdultBranches param type.
      type PendingEvent = Parameters<typeof scoreAdultBranches>[2][number];
      const pendingEvents = ((agentRec['pendingCommentableEvents'] ?? []) as PendingEvent[]);
      scores = scoreAdultBranches(perception, lastCommentaryTick, pendingEvents);
    } else if (perception.self.role === 'hunter') {
      // CR-02: pass per-entry duplicate counts from agent context (populated by
      // tick.ts BEFORE decide() so argmaxBranch sees the real AI-12 utility).
      // D-10: look up the hunter's profile for hear.stat-dependent listen weighting
      // (same hasProfile + DEFAULT_CHARACTER_ID fallback as the hider scorer below).
      const hunterProfileId = hasProfile(perception.self.characterId)
        ? perception.self.characterId
        : DEFAULT_CHARACTER_ID;
      const hunterProfile = getProfile(hunterProfileId);
      // D-34/AI2-05 + late-round idle fix (debug session late-round-idle, 2026-07-06):
      // Two related problems required the fix below.
      //
      // PROBLEM 1 — all-spots-checked → listen wins permanently:
      // checkedHidingSpotIds accumulated all spot visits but never reset mid-round.
      // At fraction=1, check=0.35 < listen=0.5975 (median hunter, stale blackboard)
      // → flick-torch-to-listen wins → StopMovement zeroes intent.move → hunter
      // idles indefinitely. HasUncheckedSpots also returns false (BT gate), so check
      // branch fails even if it scores. Reset the set to 0 when full so the hunter
      // starts a fresh sweep: HasUncheckedSpots becomes true again and check wins.
      //
      // PROBLEM 2 — early-listen during the sweep (gradient penalty premature):
      // P4_CHECKED_SPOT_PENALTY was applied linearly: check = 0.65 - 0.3*fraction.
      // For median hunter (hear.stat=0.55): listen wins when fraction > 0.175 (just
      // 2 of 11 spots on Mouchao). The hunter checked 2 spots then froze in listen
      // mode — far short of finding the last hider. Fix: always pass fraction=0 to
      // scoreHunterBranches when spots exist. This keeps check=0.65 throughout the
      // sweep, beating listen=0.5975 for hunters with hear.stat ≤ 0.667 (design
      // intent: ze at 0.70 still listens by archetype, tommy at 0.50 always checks).
      // scoreHunterBranches still supports the gradient for callers that need it
      // (e.g. unit tests); the controller passes 0 to implement continuous re-sweep.
      //
      // When totalSpots=0 (no spots on map): pass fraction=1 so check scores 0.35;
      // HasUncheckedSpots is also false so the BT sequence fails anyway — this case
      // is a no-op belt-and-suspenders per D-07.
      const totalSpots = perception.map.hidingSpots?.length ?? 0;
      if (totalSpots > 0 && this.agent.checkedHidingSpotIds.size >= totalSpots) {
        this.agent.checkedHidingSpotIds.clear(); // restart sweep: HasUncheckedSpots → true
      }
      // Always pass fraction=0 when spots exist: keep check=0.65 > listen throughout.
      const scoringFraction = totalSpots > 0 ? 0 : 1;
      scores = scoreHunterBranches(
        perception,
        this.agent.investigateDuplicateCounts,
        hunterProfile,
        scoringFraction,
      );
    } else {
      // WR-09 / WR-10 fix: read totalHiders from perception.round (populated by
      // buildPerception) and use the real CharacterProfile instead of the old
      // stubProfile — so per-character archetype tendencies (deep-hider,
      // constant-mover, liberator) actually manifest in AI decisions.
      // Debug session freed-hiders-dont-disperse (2026-04-28): the original
      // map-centre stub was a Plan 10 TODO that never landed. On Mouchao the
      // plinth zone centroid is ~333 px from map centre, so the scorer's
      // distNorm and the liberate-branch path target diverged from the actual
      // plinth, leaving freed hiders frozen near map centre instead of re-
      // hiding. Use the real zone centroid; fall back to map centre only when
      // the map has no plinth zone (test fixtures).
      const plinthZone = perception.map.zones.get('plinth');
      const plinthPos = plinthZone
        ? zoneCenter(plinthZone)
        : {
            x: (perception.map.widthInTiles * perception.map.tileWidth) / 2,
            y: (perception.map.heightInTiles * perception.map.tileHeight) / 2,
          };
      // Plan 06-13 (UAT Gap 7): wire caughtAtBaseCount from perception.round
      // (was always 0 — the original Plan-10 deferral that caused freed hiders
      // to freeze at the plinth). Both the agent scratch (consumed by the
      // HasCaughtTeammatesAtBase BT condition) AND the local utility-score
      // input must read the SAME fresh value so scoring and BT-guarding agree.
      const caughtAtBase = perception.round.caughtHidersAtBase ?? 0;
      (this.agent as Record<string, unknown>).caughtAtBaseCount = caughtAtBase;
      // WR-09: perception.round.totalHiders is populated by buildPerception; fall
      // back to 4 when absent (test fixtures that construct Perception directly).
      const totalHiders = perception.round.totalHiders ?? 4;
      const profileId = hasProfile(perception.self.characterId)
        ? perception.self.characterId
        : DEFAULT_CHARACTER_ID;
      const profile = getProfile(profileId);
      // Debug session cascade-prevention (2026-04-30): suppress the liberate
      // archetype PERMANENTLY for the rest of the round once a hider has
      // been involved in a free-all event (either as a freed captive OR as
      // the liberator — applyFreeAll sets freedImmunityUntilTick on both).
      // Earlier iterations used a 90-tick window matched to the catch-
      // immunity duration; user observation 2026-04-30 was that the same
      // hider kept returning to the ring after suppression lifted. Permanent
      // round-scoped suppression turns "freed" / "has liberated" hiders
      // into pure deep/move agents until team-switch clears the field. They
      // run away and stay away, which is the user-requested behaviour.
      // Catch immunity is unaffected — catch.ts still uses
      // (currentTick >= freedImmunityUntilTick) so the hunter can re-catch
      // them once the catch-side window expires.
      const recentlyFreedSuppressLiberate = perception.self.freedImmunityUntilTick !== null;
      scores = scoreHiderBranches(
        perception,
        profile,
        caughtAtBase,
        totalHiders,
        plinthPos,
        recentlyFreedSuppressLiberate,
      );
    }
    this.agent.utilities = scores;
    this.agent.winningBranch = argmaxBranch(scores, perception.rng);
    this.agent.perception = perception;

    // 2. Reset intent scratch before each step.
    // facing defaults to self.facing (not 0) so the listen/stop case preserves the
    // entity's current facing instead of slowly rotating toward east (0 rad).
    // Debug session hunter-no-chase-facing (2026-07-04): using 0 caused the torch
    // cone to always lerp east during non-chase branches.
    const facingBeforeBt = self.facing;
    this.agent.intent = {
      move: { x: 0, y: 0 },
      facing: facingBeforeBt,
      actions: { ...ZERO_INTENT.actions },
    };

    // 3. Step the role-appropriate tree — exactly one winning branch runs its actions
    activeTree.step();

    // 3a. Post-BT facing: if the entity has movement intent AND no BT action explicitly
    // changed the facing (detected by comparing against the pre-BT value), derive facing
    // from the movement direction so the torch cone tracks the travel direction during
    // non-chase branches (patrol, investigate-sighting, check-hiding-spot).
    //
    // FaceNearestHider (chase branch) always sets facing to point at the hider BEFORE
    // PathTowardNearestHider sets move, so after the BT step the facing will differ from
    // facingBeforeBt and this override does NOT fire — the hider-targeted facing is kept.
    //
    // For patrol/investigate/check branches that only set move: facing still equals
    // facingBeforeBt, so the override fires and aligns the torch cone with travel direction.
    //
    // For the listen branch (StopMovement + zero move): move = {0,0}, no override fires,
    // intent.facing = facingBeforeBt → lerpFacing preserves the current entity facing.
    //
    // Debug session hunter-no-chase-facing (2026-07-04).
    {
      const m = this.agent.intent.move;
      const hasMoveIntent = Math.abs(m.x) > 0.001 || Math.abs(m.y) > 0.001;
      if (hasMoveIntent && this.agent.intent.facing === facingBeforeBt) {
        this.agent.intent.facing = Math.atan2(m.y, m.x);
      }
    }

    // 4. Advance cadence (D-05: per-role decide periods)
    // WR-08 fix: role is authoritative — dropped characterId.startsWith fallback.
    const decidePeriod =
      perception.self.role === 'dog'
        ? P4_DOG_DECIDE_PERIOD_TICKS
        : perception.self.role === 'adult'
          ? P4_ADULT_DECIDE_PERIOD_TICKS
          : perception.self.role === 'hunter'
            ? P4_HUNTER_DECIDE_PERIOD_TICKS
            : P4_HIDER_DECIDE_PERIOD_TICKS;
    this.nextDecideTick = perception.tick + decidePeriod;
    this.cachedIntent = this.agent.intent;
    return this.agent.intent;
  }

  reset(): void {
    this.hunterTree.reset();
    this.hiderTree.reset();
    this.dogTree.reset();
    this.adultTree.reset();
    this.agent.checkedHidingSpotIds.clear();
    delete (this.agent as Record<string, unknown>)['_hidingStartTick']; // D-13 boredom — no stale timer across rounds
    this.cachedIntent = { ...ZERO_INTENT, actions: { ...ZERO_INTENT.actions } };
    this.nextDecideTick = 0;
  }

  /**
   * UAT Test 2 follow-up: compute the pixel target an escorting hunter should
   * walk to. Using the plinth centroid drops the hunter inside the polygon,
   * which triggers applyDropOff (catch/drop-off.ts). Kept as a small method
   * so a future "stand just outside plinth to drop off" tweak has one place
   * to change.
   */
  private escortTargetForPlinth(plinth: ZonePolygon): { x: number; y: number } {
    return zoneCenter(plinth);
  }

  /**
   * Returns a unit-vector pointing toward the next A* waypoint from `from`
   * to `to` on the perception's walkability grid, or null when the endpoints
   * share a tile (caller's "already arrived" branch) or no path exists.
   * Mirrors the helper in src/sim/ai/actions.ts but scoped to this module so
   * the escort short-circuit in decide() does not depend on a private
   * internal there.
   */
  private escortAStarDir(
    from: { x: number; y: number },
    to: { x: number; y: number },
    perception: Perception,
  ): { x: number; y: number } | null {
    const tileW = perception.map.tileWidth;
    const tileH = perception.map.tileHeight;
    const fx = Math.floor(from.x / tileW);
    const fy = Math.floor(from.y / tileH);
    const tx = Math.floor(to.x / tileW);
    const ty = Math.floor(to.y / tileH);
    if (fx === tx && fy === ty) return null;
    // Phase 08.1 Item 2: use movementGrid so AI paths around obstacles/fences.
    const path = findTilePath(fx, fy, tx, ty, perception.map.movementGrid ?? perception.map.grid);
    if (path.length < 2) return null;
    const [nx, ny] = path[1]!;
    const targetPx = {
      x: nx * tileW + tileW / 2,
      y: ny * tileH + tileH / 2,
    };
    const dx = targetPx.x - from.x;
    const dy = targetPx.y - from.y;
    const len = Math.hypot(dx, dy);
    if (len < 0.001) return null;
    return { x: dx / len, y: dy / len };
  }
}

// ---------------------------------------------------------------------------
// Tree dispatch helper (Plan 08, D-04, D-07)
// ---------------------------------------------------------------------------

/**
 * determineActiveTree — returns the correct BehaviourTree for the given agent self.
 * WR-08 fix: dispatch strictly by role (per D-04). Removed the characterId prefix
 * fallback which was a Plan 08 bridge that would misdispatch a future profile id
 * beginning with "dog" or "adult" (e.g. "dougal", "adalberto").
 */
function determineActiveTree(
  self: { role: string },
  ctrl: AIController,
): BehaviourTree {
  if (self.role === 'dog') return (ctrl as unknown as { dogTree: BehaviourTree }).dogTree;
  if (self.role === 'adult') return (ctrl as unknown as { adultTree: BehaviourTree }).adultTree;
  return self.role === 'hunter'
    ? (ctrl as unknown as { hunterTree: BehaviourTree }).hunterTree
    : (ctrl as unknown as { hiderTree: BehaviourTree }).hiderTree;
}
